import { cx } from "../../lib/cx.js";
import { Icon } from "../Icon/Icon.js";
import { Button } from "../Button/Button.js";
import { IconButton } from "../IconButton/IconButton.js";
import { Alert } from "../Alert/Alert.js";
import { Skeleton } from "../Skeleton/Skeleton.js";
/* empty css          */
import { Children, createContext, useContext, useId, useLayoutEffect, useState } from "react";
import { Fragment as Fragment$1, jsx, jsxs } from "react/jsx-runtime";
//#region src/components/Table/Table.tsx
/** Whether the parts are in the head (cells are column headers) and the responsive mode. */
var SectionContext = createContext("body");
var ResponsiveContext = createContext("scroll");
/** ARIA roles for card mode, where CSS display changes would drop table semantics. */
function cardRole(responsive, role) {
	return responsive === "cards" ? { role } : {};
}
/**
* Whether `region` scrolls sideways, kept current with a ResizeObserver on it and its
* table. Without a ResizeObserver (jsdom), it doesn't.
*/
function useScrollsInline(region, enabled) {
	const [scrolls, setScrolls] = useState(false);
	useLayoutEffect(() => {
		if (!enabled || !region || typeof ResizeObserver === "undefined") return;
		const measure = () => setScrolls(region.scrollWidth > region.clientWidth);
		measure();
		const observer = new ResizeObserver(measure);
		observer.observe(region);
		if (region.firstElementChild) observer.observe(region.firstElementChild);
		return () => observer.disconnect();
	}, [region, enabled]);
	return enabled && scrolls;
}
/**
* A presentational table (not a data grid): a labelled region with a real `<table>`.
* Compose it from TableHead, TableBody, TableRow and TableCell, with TableTotalRow,
* TableEmpty, TableLoading and TableError for the rest. No sorting (decision #35).
*/
function Table({ "aria-label": ariaLabel, caption, layout = "auto", responsive = "scroll", busy = false, className, children, ...rest }) {
	const [region, setRegion] = useState(null);
	const cardsScroll = useScrollsInline(region, responsive === "cards");
	return /* @__PURE__ */ jsx(ResponsiveContext.Provider, {
		value: responsive,
		children: /* @__PURE__ */ jsx("div", {
			...rest,
			role: "region",
			"aria-label": ariaLabel,
			ref: setRegion,
			tabIndex: responsive === "scroll" || cardsScroll ? 0 : void 0,
			className: cx("ds-table", `ds-table--${responsive}`, className),
			children: /* @__PURE__ */ jsxs("table", {
				className: cx("ds-table__table", layout === "fixed" && "ds-table__table--fixed"),
				"aria-busy": busy || void 0,
				...cardRole(responsive, "table"),
				children: [caption && /* @__PURE__ */ jsx("caption", {
					className: "ds-table__caption",
					children: caption
				}), children]
			})
		})
	});
}
/** The header row: its TableCells are column headers (`th scope="col"`). */
function TableHead({ className, children, ...rest }) {
	const responsive = useContext(ResponsiveContext);
	return /* @__PURE__ */ jsx(SectionContext.Provider, {
		value: "head",
		children: /* @__PURE__ */ jsx("thead", {
			...rest,
			className: cx("ds-table__head", className),
			...cardRole(responsive, "rowgroup"),
			children: /* @__PURE__ */ jsx("tr", {
				...cardRole(responsive, "row"),
				children
			})
		})
	});
}
/** The rows. */
function TableBody({ className, ...rest }) {
	const responsive = useContext(ResponsiveContext);
	return /* @__PURE__ */ jsx(SectionContext.Provider, {
		value: "body",
		children: /* @__PURE__ */ jsx("tbody", {
			...rest,
			className: cx("ds-table__body", className),
			...cardRole(responsive, "rowgroup")
		})
	});
}
/** A cell; in TableHead, a column header. */
function TableCell({ numeric = false, tone, width, rowHeader = false, label, stacked = false, cardsOnly = false, className, style, children, ...rest }) {
	const section = useContext(SectionContext);
	const responsive = useContext(ResponsiveContext);
	const isHeader = section === "head";
	const Element = isHeader || rowHeader ? "th" : "td";
	const role = isHeader ? "columnheader" : rowHeader ? "rowheader" : "cell";
	return /* @__PURE__ */ jsx(Element, {
		...rest,
		...isHeader ? { scope: "col" } : rowHeader ? { scope: "row" } : {},
		...cardRole(responsive, role),
		"data-label": label,
		style: width ? {
			...style,
			inlineSize: width
		} : style,
		className: cx(isHeader ? "ds-table__th" : "ds-table__td", numeric && "ds-table__cell--numeric", tone && `ds-table__cell--${tone}`, cardsOnly && "ds-table__cell--cards-only", className),
		children: stacked ? /* @__PURE__ */ jsx("div", {
			className: "ds-table__stack",
			children
		}) : children
	});
}
/**
* Part of a cell shown in the table and left out of cards, where the same values have
* their own `cardsOnly` rows (Line-item Search's configured columns, A16).
*/
function TableOnly({ className, ...rest }) {
	return /* @__PURE__ */ jsx("div", {
		className: cx("ds-table__table-only", className),
		...rest
	});
}
var INTERACTIVE = "a[href], button, input, select, textarea, [tabindex]";
/**
* A row. With `onClick` or `href` it opens something: the last cell holds a chevron
* button or link (the keyboard's way), and a click anywhere on the row does the same.
* With `detail` it expands: the last cell holds a disclosure button, and the detail shows
* in a row of its own under it.
*/
function TableRow({ children, onClick, href, as, label, detail, expanded = false, onToggle, className, ...rest }) {
	const id = useId();
	const responsive = useContext(ResponsiveContext);
	const expandable = detail !== void 0;
	const opens = onClick !== void 0 || href !== void 0;
	const columns = Children.count(children) + (expandable || opens ? 1 : 0);
	const activate = (event) => {
		const control = event.target.closest(INTERACTIVE);
		if (control && event.currentTarget.contains(control)) return;
		if (expandable) onToggle?.();
		else if (onClick) onClick();
		else event.currentTarget.querySelector(".ds-table__open")?.click();
	};
	const Link = as ?? "a";
	let control = null;
	if (expandable) control = /* @__PURE__ */ jsx(IconButton, {
		icon: "chevron-right",
		size: "sm",
		className: cx("ds-table__toggle", expanded && "ds-table__toggle--open"),
		"aria-label": label ?? "Show details",
		"aria-expanded": expanded,
		"aria-controls": `${id}detail`,
		onClick: onToggle
	});
	else if (href !== void 0) control = /* @__PURE__ */ jsx(Link, {
		href,
		className: "ds-table__open",
		"aria-label": label,
		children: /* @__PURE__ */ jsx(Icon, { name: "chevron-right" })
	});
	else if (onClick) control = /* @__PURE__ */ jsx(IconButton, {
		icon: "chevron-right",
		size: "sm",
		className: "ds-table__open",
		"aria-label": label ?? "Open",
		onClick
	});
	return /* @__PURE__ */ jsxs(Fragment$1, { children: [/* @__PURE__ */ jsxs("tr", {
		...rest,
		...cardRole(responsive, "row"),
		className: cx("ds-table__row", (opens || expandable) && "ds-table__row--clickable", expandable && expanded && "ds-table__row--expanded", className),
		onClick: opens || expandable ? activate : void 0,
		children: [children, control && /* @__PURE__ */ jsx("td", {
			className: "ds-table__td ds-table__control",
			...cardRole(responsive, "cell"),
			children: control
		})]
	}), expandable && /* @__PURE__ */ jsx("tr", {
		id: `${id}detail`,
		className: "ds-table__detail-row",
		hidden: !expanded,
		...cardRole(responsive, "row"),
		children: /* @__PURE__ */ jsx("td", {
			colSpan: columns,
			className: "ds-table__detail",
			...cardRole(responsive, "cell"),
			children: detail
		})
	})] });
}
/** The emphasized total at the bottom: a rule above, strong text. */
function TableTotalRow({ className, ...rest }) {
	const responsive = useContext(ResponsiveContext);
	return /* @__PURE__ */ jsx("tr", {
		...rest,
		...cardRole(responsive, "row"),
		className: cx("ds-table__row", "ds-table__total", className)
	});
}
/** A row across the table for when there are no rows: put an EmptyState in it. */
function TableEmpty({ colSpan, children, className }) {
	const responsive = useContext(ResponsiveContext);
	return /* @__PURE__ */ jsx("tr", {
		className: cx("ds-table__full", className),
		...cardRole(responsive, "row"),
		children: /* @__PURE__ */ jsx("td", {
			colSpan,
			...cardRole(responsive, "cell"),
			children
		})
	});
}
/** Skeleton rows while the data loads, with a status message. Set Table's `busy`. */
function TableLoading({ colSpan, rows = 3, label = "Loading rows", className }) {
	return /* @__PURE__ */ jsxs(Fragment$1, { children: [Array.from({ length: rows }, (_, row) => /* @__PURE__ */ jsx("tr", {
		className: cx("ds-table__row", "ds-table__loading", className),
		children: Array.from({ length: colSpan }, (_, cell) => /* @__PURE__ */ jsx("td", {
			className: "ds-table__td",
			children: /* @__PURE__ */ jsx(Skeleton, { variant: "text" })
		}, cell))
	}, row)), /* @__PURE__ */ jsx("tr", {
		className: "ds-table__status-row",
		children: /* @__PURE__ */ jsx("td", {
			colSpan,
			children: /* @__PURE__ */ jsx("span", {
				role: "status",
				className: "ds-table__sr",
				children: label
			})
		})
	})] });
}
/** A row across the table when loading failed: an inline danger Alert with Retry. */
function TableError({ colSpan, message = "The rows couldn’t be loaded.", onRetry, className }) {
	const responsive = useContext(ResponsiveContext);
	return /* @__PURE__ */ jsx("tr", {
		className: cx("ds-table__full", className),
		...cardRole(responsive, "row"),
		children: /* @__PURE__ */ jsx("td", {
			colSpan,
			...cardRole(responsive, "cell"),
			children: /* @__PURE__ */ jsx(Alert, {
				tone: "danger",
				action: onRetry && /* @__PURE__ */ jsx(Button, {
					size: "sm",
					variant: "secondary",
					onClick: onRetry,
					children: "Retry"
				}),
				children: message
			})
		})
	});
}
//#endregion
export { Table, TableBody, TableCell, TableEmpty, TableError, TableHead, TableLoading, TableOnly, TableRow, TableTotalRow };

//# sourceMappingURL=Table.js.map